import React, { useRef, useState } from 'react';

import { motion } from 'framer-motion';
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  HelpCircle,
  Info,
  PieChart as RadarChartIcon,
  Smartphone,
  TrendingUp,
} from 'lucide-react';
import {
  Legend,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

import {
  dimensionToSourceMap,
  multidimensionalImpactSources,
} from '../data/multidimensionalImpactSources';
import {
  getTouchFriendlyStyles,
  useIsTouchDevice,
  useSwipe,
  useViewport,
} from '../utils/mobileOptimization';

interface MultidimensionalImpactChartProps {
  className?: string;
}

interface RadarPayload {
  subject: string;
  A: number;
  B: number;
  fullMark: number;
}

// Modificamos la interfaz para que sea compatible con MouseEvent de React
interface RadarClickEvent extends React.MouseEvent<SVGElement, MouseEvent> {
  payload?: RadarPayload;
  subject?: string;
}

const MultidimensionalImpactChart: React.FC<MultidimensionalImpactChartProps> = ({
  className = '',
}) => {
  // Estados para controlar la visualización de información
  const [activeDimension, setActiveDimension] = useState<string | null>(null);
  const [showSources, setShowSources] = useState<boolean>(false);
  const [showComparison, setShowComparison] = useState<boolean>(true);

  // Usar el hook de viewport para detectar el tamaño de pantalla
  const viewport = useViewport();
  const isMobile = viewport.width < 768;
  const isTouch = useIsTouchDevice();

  // Referencia al contenedor del gráfico para gestos táctiles
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // Función para navegar entre dimensiones con gestos
  const navigateDimensions = (direction: 'next' | 'prev') => {
    if (!activeDimension) {
      // Si no hay dimensión activa, seleccionar la primera o la última
      setActiveDimension(
        direction === 'next' ? impactData[0].subject : impactData[impactData.length - 1].subject
      );
      return;
    }

    const currentIndex = impactData.findIndex(item => item.subject === activeDimension);
    if (currentIndex === -1) return;

    let newIndex;
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % impactData.length;
    } else {
      newIndex = (currentIndex - 1 + impactData.length) % impactData.length;
    }

    setActiveDimension(impactData[newIndex].subject);
  };

  // Configurar gestos táctiles para navegación
  const { handleTouchStart, handleTouchMove, handleTouchEnd } = useSwipe({
    onSwipeLeft: () => isMobile && navigateDimensions('next'),
    onSwipeRight: () => isMobile && navigateDimensions('prev'),
    threshold: 30,
  });

  // Datos mejorados para el gráfico de radar - Impacto multidimensional
  const impactData = [
    {
      subject: 'Eficiencia',
      A: 85,
      B: 40,
      fullMark: 100,
      description: 'Optimización de procesos y recursos',
      impacto: 'Alto',
      tiempoImplementacion: '3-6 meses',
      roi: '+112%',
    },
    {
      subject: 'Innovación',
      A: 75,
      B: 30,
      fullMark: 100,
      description: 'Capacidad para desarrollar nuevas soluciones',
      impacto: 'Medio-Alto',
      tiempoImplementacion: '6-12 meses',
      roi: '+150%',
    },
    {
      subject: 'Escalabilidad',
      A: 90,
      B: 35,
      fullMark: 100,
      description: 'Capacidad de crecimiento sin fricciones',
      impacto: 'Alto',
      tiempoImplementacion: '2-4 meses',
      roi: '+157%',
    },
    {
      subject: 'Seguridad',
      A: 80,
      B: 45,
      fullMark: 100,
      description: 'Protección de datos y sistemas',
      impacto: 'Crítico',
      tiempoImplementacion: '1-3 meses',
      roi: '+77%',
    },
    {
      subject: 'Experiencia',
      A: 95,
      B: 50,
      fullMark: 100,
      description: 'Satisfacción de usuarios y clientes',
      impacto: 'Alto',
      tiempoImplementacion: '2-5 meses',
      roi: '+90%',
    },
    {
      subject: 'Costos',
      A: 70,
      B: 60,
      fullMark: 100,
      description: 'Reducción de gastos operativos',
      impacto: 'Medio-Alto',
      tiempoImplementacion: '3-8 meses',
      roi: '+16%',
    },
  ];

  // Paleta de colores mejorada para un aspecto más profesional
  const COLORS = {
    primary: '#8884d8',
    secondary: '#82ca9d',
    primaryLight: 'rgba(136, 132, 216, 0.6)',
    secondaryLight: 'rgba(130, 202, 157, 0.6)',
    primaryDark: '#6b68ac',
    secondaryDark: '#5da87c',
    highlight: '#ff7300',
    background: 'rgba(255, 255, 255, 0.95)',
  };

  // Función para mostrar información detallada al hacer clic en una dimensión
  const handleDimensionClick = (event: RadarClickEvent | string) => {
    if (typeof event === 'string') {
      // Si se pasa directamente el nombre de la dimensión
      setActiveDimension(activeDimension === event ? null : event);
    } else if (event) {
      // Si se pasa un evento del gráfico de radar
      // Verificamos si tiene payload (primer Radar) o subject (segundo Radar)
      if (event.payload) {
        const dimension = event.payload.subject;
        setActiveDimension(activeDimension === dimension ? null : dimension);
      } else if (event.subject) {
        const dimension = event.subject;
        setActiveDimension(activeDimension === dimension ? null : dimension);
      }
    }
  };

  // No necesitamos una función de hover específica

  // Información detallada y enriquecida para cada dimensión
  const dimensionDetails: Record<string, string> = {
    Eficiencia:
      'La mejora del 85% en eficiencia (vs. 40% sin optimización) se traduce en una reducción del 45% en tiempo de ejecución de procesos críticos. Nuestros clientes reportan que tareas que antes tomaban 10 horas ahora se completan en 5.5 horas. Esta optimización libera aproximadamente 18 horas semanales por empleado, permitiendo enfocarse en actividades de mayor valor. El impacto financiero directo es de aproximadamente $3,600 mensuales por cada 10 empleados.',
    Innovación:
      'El incremento del 75% en capacidad de innovación (vs. 30% sin optimización) ha permitido a nuestros clientes desarrollar un promedio de 3-5 nuevas iniciativas por trimestre, comparado con 1-2 sin nuestras soluciones. Esto representa un aumento del 150% en la velocidad de innovación, resultando en nuevas fuentes de ingresos que representan hasta un 22% de crecimiento en ventas anuales.',
    Escalabilidad:
      'La mejora del 90% en escalabilidad (vs. 35% sin optimización) permite a las empresas aumentar su capacidad operativa hasta un 300% sin necesidad de inversiones proporcionales en infraestructura. Nuestros clientes han logrado triplicar su volumen de operaciones con solo un 30% de incremento en costos operativos, generando economías de escala significativas.',
    Seguridad:
      'El fortalecimiento del 80% en seguridad (vs. 45% sin optimización) ha reducido incidentes de seguridad en un 78% y ha eliminado casi por completo las vulnerabilidades críticas. Esto representa un ahorro promedio de $120,000 anuales en costos potenciales por brechas de seguridad y cumplimiento normativo, además de proteger la reputación de la marca.',
    Experiencia:
      'La mejora del 95% en experiencia de usuario (vs. 50% sin optimización) ha incrementado la satisfacción del cliente en 45 puntos porcentuales. Esto se traduce en un aumento del 65% en la tasa de retención y un incremento del 40% en recomendaciones. El valor de vida del cliente ha aumentado en un 85%, generando ingresos recurrentes más predecibles y sostenibles.',
    Costos:
      'La optimización del 70% en estructura de costos (vs. 60% sin optimización) representa un ahorro neto del 10% en gastos operativos totales. Para una empresa con gastos operativos de $100,000 mensuales, esto significa un ahorro anual de $120,000. El ROI promedio de esta inversión se alcanza en tan solo 7 meses, con beneficios sostenidos a largo plazo.',
  };

  return (
    <section className={`py-16 ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Impacto Multidimensional</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-4">
            Análisis detallado de cómo nuestras soluciones impactan múltiples dimensiones de su
            negocio, generando mejoras cuantificables y sostenibles a largo plazo en cada área
            clave.
          </p>
          <p className="text-md text-gray-700 max-w-2xl mx-auto italic border-l-4 border-blue-400 pl-4 py-2 bg-blue-50">
            Nuestro enfoque integral optimiza simultáneamente seis dimensiones críticas de su
            operación empresarial. Este análisis multidimensional revela el impacto holístico de
            nuestras soluciones exclusivas en comparación con enfoques tradicionales.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-br from-gray-50 to-blue-50 p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
          >
            <div
              className={`${isMobile ? 'flex flex-col space-y-3' : 'flex justify-between items-center'} mb-6`}
            >
              <h4 className="text-xl font-semibold text-gray-800 flex items-center">
                <RadarChartIcon className="h-5 w-5 mr-2 text-blue-600" />
                Impacto Multidimensional (%)
              </h4>
              <div className={`${isMobile ? 'flex flex-wrap gap-2' : 'flex space-x-2'}`}>
                <button
                  onClick={() => setShowComparison(!showComparison)}
                  className="px-3 py-1.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full flex items-center hover:bg-blue-200 transition-colors touch-target"
                >
                  <TrendingUp className="h-3 w-3 mr-1" />
                  {showComparison ? 'Ocultar comparación' : 'Mostrar comparación'}
                </button>
                <button
                  onClick={() => setShowSources(!showSources)}
                  className="px-3 py-1.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full flex items-center hover:bg-blue-200 transition-colors touch-target"
                >
                  <BookOpen className="h-3 w-3 mr-1" />
                  {showSources ? 'Ocultar fuentes' : 'Ver fuentes de datos'}
                </button>
                <span className="px-3 py-1.5 bg-green-100 text-green-800 text-xs font-medium rounded-full flex items-center">
                  <RadarChartIcon className="h-3 w-3 mr-1" /> Datos validados 2023
                </span>
              </div>
            </div>

            {/* Panel de fuentes de datos optimizado para móviles */}
            {showSources && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className={`mb-6 ${isMobile ? 'p-3' : 'p-4'} bg-blue-50 rounded-lg border border-blue-100`}
              >
                <div className="flex items-center justify-between mb-3">
                  <h5
                    className={`${isMobile ? 'text-xs' : 'text-sm'} font-semibold text-blue-800 flex items-center`}
                  >
                    <Info className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} mr-1`} /> Fuentes de datos
                    utilizadas
                  </h5>
                  <button
                    onClick={() => setShowSources(false)}
                    className="text-blue-500 hover:text-blue-700 text-xs touch-target"
                  >
                    Cerrar
                  </button>
                </div>
                <div className={`${isMobile ? 'space-y-2' : 'grid grid-cols-1 gap-3'}`}>
                  {multidimensionalImpactSources
                    .slice(0, isMobile ? 2 : multidimensionalImpactSources.length)
                    .map((source, index) => (
                      <div
                        key={index}
                        className={`bg-white ${isMobile ? 'p-2' : 'p-3'} rounded-md shadow-sm border border-blue-100`}
                      >
                        <div className="flex justify-between items-start">
                          <h6
                            className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-800`}
                          >
                            {source.name}
                          </h6>
                          <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded">
                            {source.year}
                          </span>
                        </div>
                        <p
                          className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-gray-600 mt-1 mb-2`}
                        >
                          {isMobile && source.description.length > 100
                            ? source.description.substring(0, 100) + '...'
                            : source.description}
                        </p>
                        <div className="flex justify-between items-center">
                          <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-gray-500`}>
                            {source.organization}
                          </span>
                          <a
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-blue-600 hover:text-blue-800 flex items-center touch-target`}
                          >
                            {isMobile ? 'Ver fuente' : 'Verificar fuente'}{' '}
                            <ExternalLink className="h-3 w-3 ml-1" />
                          </a>
                        </div>
                      </div>
                    ))}
                  {isMobile && multidimensionalImpactSources.length > 2 && (
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] text-gray-500">
                        {multidimensionalImpactSources.length - 2} fuentes más disponibles
                      </span>
                      <button
                        onClick={() => setShowSources(false)}
                        className="text-blue-600 text-xs py-1 px-2 hover:text-blue-800 transition-colors bg-white rounded-md shadow-sm border border-blue-100 touch-target"
                      >
                        Cerrar
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Indicador de versión móvil mejorado */}
            {isMobile && (
              <div className="mb-4 flex items-center justify-center bg-blue-50 p-2 rounded-lg shadow-sm border border-blue-100">
                <Smartphone className="h-4 w-4 mr-2 text-blue-600" />
                <span className="text-xs text-blue-800 font-medium">
                  Versión optimizada para dispositivos móviles
                </span>
              </div>
            )}

            {/* Instrucciones para interacción táctil en móviles */}
            {isMobile && (
              <div className="mb-4 p-2 bg-gray-50 rounded-lg border border-gray-200 text-center">
                <p className="text-xs text-gray-600">Toque cada dimensión para ver detalles</p>
              </div>
            )}

            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-8"
              ref={chartContainerRef}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={getTouchFriendlyStyles(isTouch)}
            >
              <div className={`${isMobile ? 'h-72' : 'h-96'}`}>
                {' '}
                {/* Altura reducida para móviles */}
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart
                    outerRadius={isMobile ? 60 : 90}
                    width={730}
                    height={250}
                    data={impactData}
                    margin={
                      isMobile
                        ? { top: 5, right: 10, left: 10, bottom: 5 }
                        : { top: 10, right: 30, left: 30, bottom: 10 }
                    }
                  >
                    <defs>
                      <linearGradient id="colorA" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="5%" stopColor={COLORS.primaryDark} stopOpacity={0.8} />
                        <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0.8} />
                      </linearGradient>
                      <linearGradient id="colorB" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="5%" stopColor={COLORS.secondaryDark} stopOpacity={0.8} />
                        <stop offset="95%" stopColor={COLORS.secondary} stopOpacity={0.8} />
                      </linearGradient>
                      <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                        <feDropShadow dx="0" dy="0" stdDeviation="3" floodColor="rgba(0,0,0,0.3)" />
                      </filter>
                    </defs>
                    <PolarGrid stroke="#e0e0e0" />
                    <PolarAngleAxis
                      dataKey="subject"
                      tick={{ fill: '#4B5563', fontWeight: 500, fontSize: isMobile ? 10 : 12 }}
                      stroke="#9CA3AF"
                    />
                    <PolarRadiusAxis
                      angle={30}
                      domain={[0, 100]}
                      tick={{ fill: '#4B5563' }}
                      stroke="#E5E7EB"
                      tickFormatter={value => `${value}%`}
                    />
                    <Radar
                      name="Con Nuestras Soluciones"
                      dataKey="A"
                      stroke="url(#colorA)"
                      fill="url(#colorA)"
                      fillOpacity={0.6}
                      activeDot={{ r: 8, strokeWidth: 2, stroke: '#fff' }}
                      onClick={event => {
                        // Convertimos el evento de MouseEvent a nuestro tipo personalizado
                        handleDimensionClick(event as unknown as RadarClickEvent);
                      }}
                      animationDuration={1500}
                      animationEasing="ease-out"
                      style={{ filter: 'url(#shadow)' }}
                    />
                    {showComparison && (
                      <Radar
                        name="Sin Optimización"
                        dataKey="B"
                        stroke="url(#colorB)"
                        fill="url(#colorB)"
                        fillOpacity={0.6}
                        onClick={event => {
                          // Convertimos el evento de MouseEvent a nuestro tipo personalizado
                          handleDimensionClick(event as unknown as RadarClickEvent);
                        }}
                        animationDuration={1500}
                        animationEasing="ease-out"
                        animationBegin={300}
                      />
                    )}
                    <Legend
                      layout={isMobile ? 'vertical' : 'horizontal'}
                      verticalAlign={isMobile ? 'middle' : 'bottom'}
                      align={isMobile ? 'right' : 'center'}
                      wrapperStyle={
                        isMobile ? { paddingLeft: '5px', fontSize: '10px' } : { paddingTop: '20px' }
                      }
                      formatter={value => (
                        <span
                          className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700`}
                        >
                          {value}
                        </span>
                      )}
                      iconSize={isMobile ? 8 : 14}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: COLORS.background,
                        borderRadius: '8px',
                        border: '1px solid #E5E7EB',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      }}
                      formatter={(value, name, props) => {
                        const item = impactData.find(
                          item => item.subject === props.payload.subject
                        );
                        const difference = item ? item.A - item.B : 0;
                        const formattedValue = `${value}%`;

                        if (name === 'Con Nuestras Soluciones') {
                          return [
                            <div>
                              <div className="font-medium">{formattedValue}</div>
                              {showComparison && (
                                <div className="text-xs text-green-600 mt-1">
                                  +{difference}% de mejora
                                </div>
                              )}
                            </div>,
                            name,
                          ];
                        }
                        return [formattedValue, name];
                      }}
                      labelFormatter={subject => {
                        const item = impactData.find(item => item.subject === subject);
                        return (
                          <div className="font-medium text-gray-800">
                            {subject}
                            {item && (
                              <div className="text-xs font-normal mt-1 text-gray-600">
                                {item.description}
                              </div>
                            )}
                          </div>
                        );
                      }}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>

              <div className="flex flex-col justify-center">
                <h5
                  className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold text-gray-800 mb-3`}
                >
                  Análisis Multidimensional
                </h5>
                <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-600 mb-3`}>
                  Este gráfico muestra cómo nuestras soluciones generan mejoras en seis dimensiones
                  críticas para su negocio. Cada eje representa una dimensión clave donde medimos el
                  impacto porcentual de nuestras implementaciones, comparado con operaciones sin
                  optimización.
                </p>

                <div className="grid grid-cols-1 gap-2 mb-4">
                  {impactData.map((item, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ x: isMobile ? 2 : 5 }}
                      className={`${isMobile ? 'p-1.5' : 'p-2'} rounded-md cursor-pointer flex items-center touch-target ${activeDimension === item.subject ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'}`}
                      onClick={() => handleDimensionClick(item.subject)}
                    >
                      <div
                        className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} rounded-full mr-2`}
                        style={{
                          backgroundColor: index % 2 === 0 ? COLORS.primary : COLORS.secondary,
                        }}
                      ></div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center">
                          <span
                            className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-800`}
                          >
                            {item.subject}
                          </span>
                          <div className="flex items-center">
                            <span
                              className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-blue-700`}
                            >
                              {item.A}%
                            </span>
                            {showComparison && (
                              <span
                                className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-green-600 ml-1`}
                              >
                                (+{item.A - item.B}%)
                              </span>
                            )}
                          </div>
                        </div>
                        <p className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-gray-500`}>
                          {item.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                  <div className="flex items-center mb-2">
                    <HelpCircle className="h-4 w-4 text-blue-600 mr-2" />
                    <h6 className="text-sm font-medium text-blue-800">
                      ¿Cómo interpretar este gráfico?
                    </h6>
                  </div>
                  <p className="text-xs text-gray-600">
                    Cuanto mayor sea el área coloreada, mejor es el rendimiento en esa dimensión. El
                    área púrpura muestra el rendimiento con nuestras soluciones, mientras que el
                    área verde representa el rendimiento sin optimización. Haga clic en cualquier
                    dimensión para ver un análisis detallado.
                  </p>
                </div>
              </div>
            </div>

            {/* Controles de navegación táctil para móviles */}
            {/* Controles de navegación táctil para móviles */}
            {isMobile && activeDimension && (
              <div className="flex justify-center items-center mt-4 mb-2 gap-4">
                <button
                  onClick={() => navigateDimensions('prev')}
                  className="p-2 bg-blue-100 rounded-full text-blue-700 hover:bg-blue-200 transition-colors touch-target"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-xs text-gray-600">Desliza para navegar</span>
                <button
                  onClick={() => navigateDimensions('next')}
                  className="p-2 bg-blue-100 rounded-full text-blue-700 hover:bg-blue-200 transition-colors touch-target"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Panel de información detallada mejorado y optimizado para móviles */}
            {activeDimension && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                className={`mt-6 ${isMobile ? 'p-3' : 'p-4'} bg-white rounded-lg border border-gray-200 shadow-sm`}
              >
                <div className={`${isMobile ? 'flex flex-col' : 'flex items-start'}`}>
                  {!isMobile && (
                    <div
                      className="h-8 w-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0"
                      style={{
                        backgroundColor:
                          impactData.findIndex(item => item.subject === activeDimension) % 2 === 0
                            ? COLORS.primary
                            : COLORS.secondary,
                        opacity: 0.8,
                      }}
                    >
                      <Info className="h-4 w-4 text-white" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div
                      className={`${isMobile ? 'flex flex-col space-y-2' : 'flex justify-between items-center'} mb-2`}
                    >
                      <div className={`${isMobile ? 'flex items-center' : ''}`}>
                        {isMobile && (
                          <div
                            className="h-6 w-6 rounded-full flex items-center justify-center mr-2 flex-shrink-0"
                            style={{
                              backgroundColor:
                                impactData.findIndex(item => item.subject === activeDimension) %
                                  2 ===
                                0
                                  ? COLORS.primary
                                  : COLORS.secondary,
                              opacity: 0.8,
                            }}
                          >
                            <Info className="h-3 w-3 text-white" />
                          </div>
                        )}
                        <h5
                          className={`font-semibold text-gray-800 ${isMobile ? 'text-base' : 'text-lg'}`}
                        >
                          {activeDimension}
                        </h5>
                      </div>
                      <div
                        className={`${isMobile ? 'flex flex-wrap gap-1' : 'flex items-center space-x-2'}`}
                      >
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                          {impactData.find(item => item.subject === activeDimension)?.A}% con
                          optimización
                        </span>
                        {showComparison && (
                          <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">
                            {impactData.find(item => item.subject === activeDimension)?.B}% sin
                            optimización
                          </span>
                        )}
                      </div>
                    </div>

                    <div
                      className={`grid ${isMobile ? 'grid-cols-3 gap-1' : 'grid-cols-1 md:grid-cols-3 gap-3'} mb-3`}
                    >
                      <div
                        className={`bg-gray-50 ${isMobile ? 'p-1.5' : 'p-2'} rounded border border-gray-200`}
                      >
                        <span
                          className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-gray-500 block`}
                        >
                          Impacto:
                        </span>
                        <span
                          className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-800`}
                        >
                          {impactData.find(item => item.subject === activeDimension)?.impacto}
                        </span>
                      </div>
                      <div
                        className={`bg-gray-50 ${isMobile ? 'p-1.5' : 'p-2'} rounded border border-gray-200`}
                      >
                        <span
                          className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-gray-500 block`}
                        >
                          Tiempo:
                        </span>
                        <span
                          className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-800`}
                        >
                          {
                            impactData.find(item => item.subject === activeDimension)
                              ?.tiempoImplementacion
                          }
                        </span>
                      </div>
                      <div
                        className={`bg-gray-50 ${isMobile ? 'p-1.5' : 'p-2'} rounded border border-gray-200`}
                      >
                        <span
                          className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-gray-500 block`}
                        >
                          ROI:
                        </span>
                        <span
                          className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-green-700`}
                        >
                          {impactData.find(item => item.subject === activeDimension)?.roi}
                        </span>
                      </div>
                    </div>

                    <p
                      className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-600 leading-relaxed`}
                    >
                      {isMobile
                        ? dimensionDetails[activeDimension].split('. ').slice(0, 3).join('. ') + '.' // Versión resumida para móviles
                        : dimensionDetails[activeDimension]}
                      {isMobile && (
                        <button
                          className="ml-1 text-blue-600 text-xs font-medium"
                          onClick={() => setActiveDimension(null)}
                        >
                          Cerrar
                        </button>
                      )}
                    </p>

                    <div
                      className={`mt-3 ${isMobile ? 'flex flex-col space-y-1' : 'flex items-center'}`}
                    >
                      <span
                        className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-gray-500 ${isMobile ? '' : 'mr-2'}`}
                      >
                        Fuentes de datos:
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {dimensionToSourceMap[activeDimension]?.map((source, index) => (
                          <span
                            key={index}
                            className={`bg-blue-50 text-blue-700 ${isMobile ? 'text-[10px]' : 'text-xs'} px-2 py-0.5 rounded`}
                          >
                            {isMobile && source.length > 20
                              ? source.substring(0, 18) + '...'
                              : source}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            <div className="mt-4 text-center text-sm text-gray-500">
              <p>Comparación multidimensional del impacto de nuestras soluciones</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default MultidimensionalImpactChart;
